import { formatCurrency } from '../money';
import { SCHEDULE_NOTICE_QUIET_MINUTES_DEFAULT, SCHEDULE_NOTICE_QUIET_MINUTES_KEY } from './visitNotices';
import { AGREEMENT_RENEWAL_DAYS_DEFAULT, AGREEMENT_RENEWAL_DAYS_KEY } from './agreements';
import { ON_CALL_BACKUP_AFTER_DEFAULT, ON_CALL_BACKUP_AFTER_KEY } from '../work/onCall';

/**
 * Emergency service (piece 29; decisions 0732, 0734): the text that asks a
 * customer to authorise a callout, what counts as a yes, which outbound message
 * a reply answers, and when an approval is retrospective and when it is late.
 * Pure: the services resolve the rows and the clock.
 */

/** 🔒 How long an emergency approval may sit before it is late, seeded at 24 (piece 29; T391, the owner's 24-hour review; moved from 72 by T2206's batch G1). */
export const RETROSPECTIVE_WINDOW_KEY = 'approvals.retrospectiveWindowHours';
export const RETROSPECTIVE_WINDOW_DEFAULT = 24;
/** 🔒 How long after an outbound text a reply may still match it, seeded at 24 (piece 29, open issue). */
export const INBOUND_REPLY_WINDOW_KEY = 'workorder.inboundReplyWindowHours';
export const INBOUND_REPLY_WINDOW_DEFAULT = 24;

/** The Settings rows piece 29 seeds: the two emergency windows on Settings › Service › Emergency Approvals, the quiet window on Schedule Notices (decision 2250), the Agreement renewal lead (decision 3902), and the on-call backup's minutes (decisions 1640, 4001). */
export const PIECE_29_SETTINGS: readonly { key: string; value: number }[] = [
  { key: RETROSPECTIVE_WINDOW_KEY, value: RETROSPECTIVE_WINDOW_DEFAULT },
  { key: INBOUND_REPLY_WINDOW_KEY, value: INBOUND_REPLY_WINDOW_DEFAULT },
  { key: SCHEDULE_NOTICE_QUIET_MINUTES_KEY, value: SCHEDULE_NOTICE_QUIET_MINUTES_DEFAULT },
  { key: AGREEMENT_RENEWAL_DAYS_KEY, value: AGREEMENT_RENEWAL_DAYS_DEFAULT },
  { key: ON_CALL_BACKUP_AFTER_KEY, value: ON_CALL_BACKUP_AFTER_DEFAULT },
];

/** The record type a confirmation is sent about, the `MessageLog` related pair's type. */
export const EMERGENCY_CONFIRMATION_RECORD = 'WorkOrder' as const;

/**
 * 🔒 The outbound rows a reply is matched against (piece 21, *Communications
 * seam*; piece 29's reply routing; decision 5210): a text about a job, the
 * emergency confirmation among them, awaits the customer's answer. A text from
 * a Person's thread, a login code, an event reminder or any other row awaits
 * none, so a later one never takes a reply meant for the job.
 */
export const REPLY_AWAITING_RECORD_TYPES: readonly string[] = [EMERGENCY_CONFIRMATION_RECORD];

export interface ConfirmationFacts {
  customerName: string;
  siteName: string;
  fault: string;
  /** The tier's trip charge in cents; null where the tier names none. */
  calloutCents: number | null;
  /** The after-hours hourly rate in cents; null where it cannot be resolved. */
  afterHoursRateCents: number | null;
  currencyCode: string;
}

/**
 * 🔨 A confirmation names a number: the callout charge and the after-hours rate
 * as figures, with the site and the reported fault. One missing either figure
 * is refused before it is sent, because a text saying only that emergency
 * rates apply cannot support an invoice.
 */
export function textConfirmationProblem(facts: ConfirmationFacts): string | null {
  if (facts.calloutCents === null) return 'the confirmation names the callout charge as a figure, and this job’s priority tier carries none';
  if (facts.afterHoursRateCents === null) return 'the confirmation names the after-hours rate as a figure, and none resolves for this job';
  if (!facts.siteName.trim()) return 'the confirmation names the site';
  if (!facts.fault.trim()) return 'the confirmation names the reported fault';
  return null;
}

/**
 * The message itself. 🔨 It authorises a diagnosis visit at emergency rates and
 * nothing more: the repair, the parts and a replacement unit go through the
 * ordinary approval once a technician is on site.
 */
export function textConfirmationMessage(facts: ConfirmationFacts): string {
  const money = (cents: number) => formatCurrency({ amount: cents, currencyCode: facts.currencyCode });
  return [
    `${facts.customerName}: emergency callout to ${facts.siteName} for "${facts.fault.trim()}".`,
    `Callout charge ${money(facts.calloutCents ?? 0)}, then ${money(facts.afterHoursRateCents ?? 0)} per hour after hours.`,
    'This authorizes a diagnosis visit at these rates only; any repair or parts will be quoted to you first.',
    'Reply YES to authorize.',
  ].join(' ');
}

const NEGATIVE = /\b(no|nope|stop|cancel|don'?t|do not|not|decline|declined|never)\b/i;
const AFFIRMATIVE = /\b(y|yes|yeah|yep|yup|ok|okay|k|sure|confirm|confirmed|approve|approved|go|go ahead|authori[sz]e[d]?|please|pls|plz)\b/i;

/** 🔨 Any affirmative reply is accepted, *"ok"* and *"yes pls"* included; a reply carrying a no is never read as a yes. */
export function isAffirmative(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed || NEGATIVE.test(trimmed)) return false;
  return AFFIRMATIVE.test(trimmed) || /^👍/u.test(trimmed);
}

/** Digits with the leading plus, so two spellings of one number compare equal. */
export function phoneKey(number: string): string {
  const digits = number.replace(/[^0-9]/g, '');
  return digits ? `+${digits}` : '';
}

export interface OutboundForMatch {
  id: string;
  toNumber: string;
  createdAt: Date;
  /** Whether an inbound row already names this one as the message it answers. */
  answered: boolean;
}

/**
 * A reply matches the most recent unanswered outbound message to its number
 * inside the window, or nothing. A reply matching nothing is filed unmatched
 * and authorises nothing (decision 0732).
 */
export function matchReply(outbounds: readonly OutboundForMatch[], fromNumber: string, at: Date, windowHours: number): OutboundForMatch | null {
  const from = phoneKey(fromNumber);
  const earliest = at.getTime() - windowHours * 3_600_000;
  const candidates = outbounds
    .filter((row) => !row.answered && phoneKey(row.toNumber) === from && row.createdAt.getTime() <= at.getTime() && row.createdAt.getTime() >= earliest)
    .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  return candidates[0] ?? null;
}

// ---- Approve-after (decision 0734) ----

/**
 * 🔒 Only the job emergency intake raised dispatches before its approval; any
 * other waits on it. A follow-up raised from that job is the repair the text
 * never authorised, so it waits like any other (decision 0734).
 */
export function dispatchApprovalProblem(facts: { approvalStatus: string; dealOrigin: string | null; isFollowUp?: boolean }): string | null {
  if (facts.approvalStatus !== 'Pending' && facts.approvalStatus !== 'Rejected') return null;
  if (facts.dealOrigin === 'Emergency' && !facts.isFollowUp) return null;
  return facts.approvalStatus === 'Pending'
    ? 'this job waits on its approval before it dispatches; only emergency work is approved after'
    : 'this job’s approval was rejected; resubmit it before it dispatches';
}

/** Retrospective is derived, never stored: a job dispatched while its approval is Pending. */
export function isRetrospective(facts: { approvalStatus: string; dispatchedAt: Date | null }): boolean {
  return facts.approvalStatus === 'Pending' && facts.dispatchedAt !== null;
}

/** A retrospective approval is aged once it has sat longer than the window since dispatch. */
export function isAgedRetrospective(facts: { approvalStatus: string; dispatchedAt: Date | null }, now: Date, windowHours: number): boolean {
  return isRetrospective(facts) && now.getTime() - facts.dispatchedAt!.getTime() > windowHours * 3_600_000;
}

// ---- Response target ----

/** 🔨 Response is measured from creation to Dispatched, never to Complete. */
export function responseMinutes(createdAt: Date, dispatchedAt: Date | null): number | null {
  if (!dispatchedAt) return null;
  return Math.max(0, Math.round((dispatchedAt.getTime() - createdAt.getTime()) / 60_000));
}

export interface ResponseRow {
  month: string;
  tierKey: string;
  tierName: string;
  targetMinutes: number;
  jobs: number;
  met: number;
  averageMinutes: number;
}

/** The response report by month and tier, each job against the target of the tier row it was dispatched under. */
export function responseByMonth(jobs: readonly { createdAt: Date; dispatchedAt: Date | null; month: string; tierKey: string; tierName: string; targetMinutes: number }[]): ResponseRow[] {
  const buckets = new Map<string, ResponseRow & { total: number }>();
  for (const job of jobs) {
    const minutes = responseMinutes(job.createdAt, job.dispatchedAt);
    if (minutes === null) continue;
    const key = `${job.month}|${job.tierKey}|${job.targetMinutes}`;
    const row = buckets.get(key) ?? { month: job.month, tierKey: job.tierKey, tierName: job.tierName, targetMinutes: job.targetMinutes, jobs: 0, met: 0, averageMinutes: 0, total: 0 };
    row.jobs += 1;
    row.total += minutes;
    if (minutes <= job.targetMinutes) row.met += 1;
    row.averageMinutes = Math.round(row.total / row.jobs);
    buckets.set(key, row);
  }
  return [...buckets.values()].map(({ total: _total, ...row }) => row).sort((a, b) => b.month.localeCompare(a.month) || a.tierName.localeCompare(b.tierName));
}
